/**
 * Activity-layout container.
 *
 * Owns everything the Activity layout needs — scope filter state and the
 * three thread sections — so `ActiveThreadsSection` keeps one branch instead
 * of a second layout's worth of derived state. That component is already the
 * sidebar's most complex, and inlining this pushed it past the complexity
 * gate; the split is what keeps each layout readable on its own terms.
 *
 * Rendered only in Activity mode, so its hooks run all-or-nothing.
 */

import { useNowMinute } from "../../hooks/useNowMinute";
import { useSettings } from "../../hooks/useSettings";
import { useMemo, type ReactNode } from "react";
import type { SidebarThreadSortOrder } from "@cmmd-forge/contracts/settings";

import { useActivitySidebarScopeStore } from "~/activitySidebarScopeStore";
import type { SidebarThreadSummary } from "~/types";
import { ACTIVITY_ALL_PROJECTS_LABEL, ActivitySidebar } from "./ActivitySidebar";
import { useProjectDisplayNameResolver } from "../projects/useProjectDisplayNameResolver";
import {
  buildActivityProjectScopeIndex,
  buildActivityScopeProjects,
  buildActivityThreadSections,
  resolveActivityEmptyStateLabel,
  resolveActivityScopeKey,
  resolveActivityScopeLabel,
  type ActivityScopeProject,
} from "./activitySidebar.logic";
import { sortSectionThreads } from "./activeThreadsSection.logic";
import {
  resolveCanonicalProjectCmmdId,
  resolveCanonicalProjectIdentity,
} from "./projectGroupedSidebar.logic";
import type { ProjectGroup } from "./projectGroupedSidebar.logic";

export interface ActivitySidebarSectionProps {
  readonly navigationChrome: ReactNode;
  /** The same project groups the Projects layout renders as rows. */
  readonly projectGroups: readonly ProjectGroup[];
  /** Canonical CMMD projects that must not appear in ordinary project pickers. */
  readonly archivedCmmdProjectIds: ReadonlySet<number>;
  /**
   * Every sidebar thread, archived included — Activity shows archived threads
   * in their own shelf rather than dropping them the way Projects does.
   */
  readonly visibleThreads: readonly SidebarThreadSummary[];
  readonly pinnedThreadById: Readonly<Record<string, boolean>>;
  readonly threadSortOrder: SidebarThreadSortOrder;
  readonly onAddProject: () => void;
  readonly onProjectSettings: (project: ActivityScopeProject) => void;
  readonly renderThreadList: (
    sectionId: string,
    threads: readonly SidebarThreadSummary[],
    orderedThreadKeys?: readonly string[],
  ) => ReactNode;
}

export function ActivitySidebarSection(props: ActivitySidebarSectionProps) {
  const requestedScopeKey = useActivitySidebarScopeStore((state) => state.scopeKey);
  const setScopeKey = useActivitySidebarScopeStore((state) => state.setScopeKey);
  const archivedShelfExpanded = useActivitySidebarScopeStore(
    (state) => state.archivedShelfExpanded,
  );
  const toggleArchivedShelf = useActivitySidebarScopeStore((state) => state.toggleArchivedShelf);

  const resolveProjectDisplayName = useProjectDisplayNameResolver();
  const scopeProjects = useMemo<readonly ActivityScopeProject[]>(
    () =>
      buildActivityScopeProjects({
        groups: props.projectGroups,
        archivedCmmdProjectIds: props.archivedCmmdProjectIds,
        resolveCmmdProjectId: resolveCanonicalProjectCmmdId,
        resolveCanonicalIdentity: (group) => resolveCanonicalProjectIdentity(group) ?? null,
        resolveDisplayName: (group) =>
          resolveProjectDisplayName(group.representative.environmentId, group.representative.id),
      }),
    [props.archivedCmmdProjectIds, props.projectGroups, resolveProjectDisplayName],
  );

  const scopeKey = resolveActivityScopeKey({
    requestedScopeKey,
    projects: scopeProjects,
  });

  // Minute-quantised so the partition memo does not recompute every render.
  const nowMinute = useNowMinute();
  const autoSettleAfterDays = useSettings((settings) => settings.sidebarAutoSettleAfterDays);

  const projectScopeIndex = useMemo(
    () => buildActivityProjectScopeIndex(scopeProjects),
    [scopeProjects],
  );

  const sections = useMemo(
    () =>
      buildActivityThreadSections({
        threads: props.visibleThreads,
        pinnedThreadById: props.pinnedThreadById,
        scopeKey,
        projectKeyByPhysicalKey: projectScopeIndex,
        settled: { autoSettleAfterDays, now: nowMinute },
        sortThreads: (threads) => sortSectionThreads(threads, props.threadSortOrder),
      }),
    [
      props.visibleThreads,
      props.pinnedThreadById,
      props.threadSortOrder,
      scopeKey,
      projectScopeIndex,
      autoSettleAfterDays,
      nowMinute,
    ],
  );

  const scopedProjectName =
    scopeKey === null
      ? null
      : (scopeProjects.find((project) => project.projectKey === scopeKey)?.name ?? null);

  return (
    <ActivitySidebar
      navigationChrome={props.navigationChrome}
      projects={scopeProjects}
      sections={sections}
      scopeKey={scopeKey}
      scopeLabel={resolveActivityScopeLabel({
        scopeKey,
        projects: scopeProjects,
        allProjectsLabel: ACTIVITY_ALL_PROJECTS_LABEL,
      })}
      onScopeKeyChange={setScopeKey}
      archivedShelfExpanded={archivedShelfExpanded}
      onToggleArchivedShelf={toggleArchivedShelf}
      emptyStateLabel={resolveActivityEmptyStateLabel({
        hasProjects: scopeProjects.length > 0,
        scopedProjectName,
      })}
      hasProjects={scopeProjects.length > 0}
      onAddProject={props.onAddProject}
      onProjectSettings={props.onProjectSettings}
      renderThreadList={props.renderThreadList}
    />
  );
}
